import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// A dialog rendered at the document's root: a parent with a transform (e.g. a .card on hover) would otherwise
// become its containing block and clip it. Keeps focus inside, starts on the element marked data-autofocus
// (else the first field), closes on Escape or a click outside, and gives focus back to what opened it.

export function Modal({ title, onClose, children, width = 'max-w-lg' }: { title: string; onClose: () => void; children: ReactNode; width?: string }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const root = panel.current;
    const focusables = () => [...(root?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') ?? [])];
    (root?.querySelector<HTMLElement>('[data-autofocus]') ?? focusables()[0])?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const [first, last] = [list[0], list[list.length - 1]];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); opener?.focus?.(); };
  }, [onClose]);
  return createPortal(
    <div className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label={title}
        className={`animate-scale-in w-full ${width} rounded-xl p-6 shadow-xl`}
        style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)' }}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
